import React from 'react'
import { Icon } from '../Icon'
import { ShowIf } from '../ShowIf'
import styles from './FloatingActionButton.module.scss'

export type FloatingActionButtonProps = {
  /** FontAwesome icon name (e.g., "robot", "plus", "bell") */
  icon: string
  /** Click handler function */
  onClick: () => void
  /** Accessible label for screen readers — required when no `label` is set, ignored when one is */
  ariaLabel?: string
  /** Position of the FAB on screen. Default: 'bottom-right' */
  position?: 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right'
  /** ARIA expanded state (use when FAB controls a panel/modal) */
  ariaExpanded?: boolean
  /** Button size: sm=44px, md=56px, lg=72px. Default: 'md' */
  size?: 'sm' | 'md' | 'lg'
  /** Additional CSS classes */
  className?: string
  /** Badge content (numbers >99 show as "99+") */
  badge?: number | string
  /** Test ID for integration tests */
  dataTestId?: string
  /** Visible text beside the icon. With a label the text is the accessible name, so no aria-label is rendered */
  label?: string
  /** The rendered button, for a caller that moves focus back to it — prefer the forwarded ref */
  buttonRef?: React.Ref<HTMLButtonElement>
} & Pick<React.ButtonHTMLAttributes<HTMLButtonElement>, 'style' | 'onMouseDown' | 'onMouseUp' | 'onTouchEnd'>

/**
 * FloatingActionButton (FAB) component with accessibility support.
 *
 * A reusable floating action button that follows Material Design guidelines with proper
 * accessibility, keyboard navigation, and responsive sizing. Suitable for primary actions
 * like opening chat interfaces, creating new items, or showing notifications.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <FloatingActionButton
 *   icon="robot"
 *   onClick={handleClick}
 *   ariaLabel="Open AI Assistant"
 * />
 *
 * // With badge and position
 * <FloatingActionButton
 *   icon="bell"
 *   onClick={openNotifications}
 *   ariaLabel="Notifications"
 *   badge={5}
 *   position="bottom-left"
 * />
 * ```
 */
export const FloatingActionButton = React.forwardRef<HTMLButtonElement, FloatingActionButtonProps>(
  (
    {
      icon,
      onClick,
      ariaLabel,
      position = 'bottom-right',
      ariaExpanded,
      size = 'md',
      className = '',
      badge,
      dataTestId,
      label,
      buttonRef,
      style,
      onMouseDown,
      onMouseUp,
      onTouchEnd,
    },
    ref
  ) => {
    const buttonClasses = [
      styles.fab,
      styles[position],
      styles[size],
      label ? styles.labelled : undefined,
      label ? 'rounded px-3 gap-2' : 'rounded-circle',
      'shadow-lg',
      'd-flex',
      'align-items-center',
      'justify-content-center',
      'border-0',
      className,
    ]
      .filter(Boolean)
      .join(' ')

    const handleClick = () => {
      onClick()
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        onClick()
      }
    }

    const showBadge = badge !== undefined && badge !== null
    const badgeContent = typeof badge === 'number' && badge > 99 ? '99+' : badge

    return (
      <button
        type="button"
        ref={buttonRef ?? ref}
        className={buttonClasses}
        style={style}
        onClick={handleClick}
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
        onTouchEnd={onTouchEnd}
        onKeyDown={handleKeyDown}
        aria-label={label ? undefined : ariaLabel}
        aria-expanded={ariaExpanded}
        data-testid={dataTestId}
      >
        <span aria-hidden="true">
          <Icon name={icon} className={styles.icon} />
        </span>
        <ShowIf condition={!!label}>
          <span>{label}</span>
        </ShowIf>
        <ShowIf condition={showBadge}>
          <span className={`${styles.badge} badge rounded-pill bg-danger position-absolute`}>{badgeContent}</span>
        </ShowIf>
      </button>
    )
  }
)
